import { useEffect, useCallback } from 'react';
import * as blazeface from '@tensorflow-models/blazeface';
import '@tensorflow/tfjs';

function FaceAnalyzer({ image, onAnalysisComplete, onLoadingChange }) {
  
  const analyzeSkinTone = useCallback((img, topLeft, bottomRight) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 100;
    canvas.height = 100;
    
    const sampleX = topLeft[0] + (bottomRight[0] - topLeft[0]) * 0.5 - 50;
    const sampleY = topLeft[1] + (bottomRight[1] - topLeft[1]) * 0.2 - 50;
    const sampleSize = Math.min(bottomRight[0] - topLeft[0], bottomRight[1] - topLeft[1]) * 0.3;
    
    ctx.drawImage(img, sampleX, sampleY, sampleSize, sampleSize, 0, 0, 100, 100);
    
    const imageData = ctx.getImageData(0, 0, 100, 100);
    const data = imageData.data;
    
    let r = 0, b = 0;
    let count = 0;
    
    for (let i = 0; i < data.length; i += 40) {
      r += data[i];
      b += data[i + 2];
      count++;
    }
    
    r = Math.round(r / count);
    b = Math.round(b / count);
    
    const warmth = (r > b) ? 'warm' : (b > r + 15) ? 'cool' : 'neutral';
    
    if (warmth === 'warm') return 'Warm';
    if (warmth === 'cool') return 'Cool';
    return 'Neutral';
  }, []);

  const getRecommendations = useCallback((faceShape, skinTone) => {
    const styles = {
      'Oval': {
        hairstyles: ['Pixie cut', 'Long layers', 'Bob (any length)', 'Side-swept bangs', 'Beach waves', 'Sleek ponytail'],
        avoid: ['Heavy blunt bangs', 'Overly voluminous top'],
        why: 'Your balanced proportions are the most versatile. Almost any style complements your features.'
      },
      'Round': {
        hairstyles: ['Long layers below chin', 'Side part', 'Angled bob', 'Height at crown', 'Side-swept bangs', 'Textured shag'],
        avoid: ['Chin-length bobs', 'Blunt bangs', 'Round curls at cheeks', 'Center parts with no height'],
        why: 'Add angles and vertical lines to elongate your face and create definition.'
      },
      'Square': {
        hairstyles: ['Soft layers', 'Side-swept bangs', 'Beach waves', 'Textured ends', 'Long with body', 'Curtain bangs'],
        avoid: ['Blunt cuts at jaw', 'Straight bangs', 'Sleek, straight styles', 'Boxy bobs'],
        why: 'Soften your strong jawline with movement, texture, and asymmetrical elements.'
      },
      'Heart': {
        hairstyles: ['Chin-length bobs', 'Side-swept bangs', 'Layers around jaw', 'Pixie with volume at bottom', 'Long with waves', 'Bangs grazing brows'],
        avoid: ['Short, blunt bangs', 'Top-heavy styles', 'Pixies with volume on top'],
        why: 'Balance your wider forehead by adding volume and width at the jawline.'
      },
      'Oblong': {
        hairstyles: ['Bangs (blunt or side-swept)', 'Volume at sides', 'Shoulder length with waves', 'Chin-length bobs', 'Curtain bangs'],
        avoid: ['Too much height on top', 'Long straight hair', 'Sleek high ponytails', 'Vertical styles'],
        why: 'Add width to balance your face length and break up vertical lines.'
      },
      'Diamond': {
        hairstyles: ['Chin-length cuts', 'Bangs', 'Layers at cheekbones', 'Tucked behind ears', 'Bob with volume', 'Side parts'],
        avoid: ['Short layers at cheekbones', 'Overly wide styles', 'Volume on sides'],
        why: 'Show off your prominent cheekbones while softening angular features.'
      },
      'Triangle': {
        hairstyles: ['Layers at crown', 'Side-swept bangs', 'Volume at top', 'Long layers', 'Textured ends', 'Deep side part'],
        avoid: ['Sleek, straight styles', 'Volume at jaw', 'Blunt cuts at chin'],
        why: 'Balance your wider jaw with volume and interest at the crown.'
      },
      'Rectangle': {
        hairstyles: ['Soft waves', 'Side parts', 'Layers', 'Bangs', 'Volume at sides', 'Shoulder length'],
        avoid: ['Too long/straight', 'Height at crown', 'Sleek pulled-back styles'],
        why: 'Soften angular features and add width to balance face length.'
      }
    };

    const colors = {
      'Warm': {
        palette: ['Honey blonde', 'Golden brown', 'Copper', 'Warm black', 'Caramel highlights', 'Auburn'],
        avoid: ['Ash tones', 'Platinum blonde', 'Blue-black', 'Silver'],
        why: 'Golden, warm tones complement your skin\'s natural warmth.'
      },
      'Cool': {
        palette: ['Ash blonde', 'Platinum', 'Espresso', 'Burgundy', 'Jet black', 'Silver'],
        avoid: ['Golden tones', 'Copper', 'Yellow-based brunette', 'Warm reds'],
        why: 'Cool, ashy tones enhance your natural undertones.'
      },
      'Neutral': {
        palette: ['Beige blonde', 'Chestnut', 'Chocolate brown', 'Most shades work', 'Balayage', 'Soft black'],
        avoid: ['Extreme warm or cool tones'],
        why: 'You can wear both warm and cool tones—experiment to find your favorites!'
      }
    };

    return {
      faceShape: styles[faceShape] || styles['Oval'],
      skinTone: colors[skinTone] || colors['Neutral']
    };
  }, []);

  const performAnalysis = useCallback((face, img) => {
    const topLeft = face.topLeft;
    const bottomRight = face.bottomRight;
    
    const faceWidth = bottomRight[0] - topLeft[0];
    const faceHeight = bottomRight[1] - topLeft[1];
    const ratio = faceWidth / faceHeight;
    
    let faceShape, characteristics, confidence;
    
    if (ratio > 0.92 && ratio < 1.08) {
      if (faceHeight > faceWidth * 1.25) {
        faceShape = 'Oblong';
        characteristics = 'Your face length is significantly greater than width. Your forehead, cheeks, and jawline are similar in width, creating an elongated appearance.';
        confidence = 'high';
      } else if (faceHeight < faceWidth * 1.1) {
        faceShape = 'Round';
        characteristics = 'Your face width and length are nearly equal with soft, curved lines. Full cheeks and a rounded chin give you a youthful, friendly appearance.';
        confidence = 'high';
      } else {
        faceShape = 'Oval';
        characteristics = 'Your face is slightly longer than wide with balanced proportions. This versatile shape is considered the ideal balance of features.';
        confidence = 'high';
      }
    } else if (ratio >= 1.08) {
      if (faceWidth * 0.85 > faceHeight * 0.75) {
        faceShape = 'Square';
        characteristics = 'You have a strong, angular jawline with forehead and jaw nearly equal in width. Your features create a powerful, structured appearance.';
        confidence = 'high';
      } else {
        faceShape = 'Rectangle';
        characteristics = 'Your face is longer than wide with angular features. Your forehead and jawline are similar in width, creating a structured, athletic look.';
        confidence = 'medium';
      }
    } else {
      const topSection = faceHeight * 0.3;
      const bottomSection = faceHeight * 0.35;
      
      if (topSection > bottomSection * 1.1) {
        faceShape = 'Heart';
        characteristics = 'You have a wider forehead that gracefully tapers to a narrower, often pointed chin. Your cheekbones are likely prominent and eye-catching.';
        confidence = 'high';
      } else if (bottomSection > topSection * 1.15) {
        faceShape = 'Triangle';
        characteristics = 'Your face features a narrower forehead with a wider, stronger jawline. This creates a grounded, determined appearance.';
        confidence = 'medium';
      } else {
        const midWidth = faceWidth * 1.05;
        if (midWidth > faceWidth) {
          faceShape = 'Diamond';
          characteristics = 'Your face is widest at the cheekbones with a narrow forehead and jawline. This creates striking, dramatic features with prominent cheekbones.';
          confidence = 'high';
        } else {
          faceShape = 'Oval';
          characteristics = 'Your face has balanced proportions with gentle curves. This versatile shape works with nearly any hairstyle.';
          confidence = 'medium';
        }
      }
    }

    const skinTone = analyzeSkinTone(img, topLeft, bottomRight);
    
    return {
      faceShape,
      characteristics,
      confidence,
      skinTone,
      ratio: ratio.toFixed(2),
      recommendations: getRecommendations(faceShape, skinTone)
    };
  }, [analyzeSkinTone, getRecommendations]);

  useEffect(() => {
    const analyzeFace = async () => {
      onLoadingChange(true);
      
      try {
        const model = await blazeface.load();
        
        const img = new Image();
        img.src = image;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const predictions = await model.estimateFaces(img, false);
        
        if (predictions.length === 0) {
          onAnalysisComplete({
            error: "No face detected. Please try another photo with a clear, front-facing view."
          });
          return;
        }

        if (predictions.length > 1) {
          onAnalysisComplete({
            error: "Multiple faces detected. Please upload a photo with just one face."
          });
          return;
        }

        const face = predictions[0];
        const analysis = performAnalysis(face, img);
        onAnalysisComplete(analysis);
        
      } catch (error) {
        console.error('Analysis error:', error);
        onAnalysisComplete({
          error: "Analysis failed. Please try again with a clearer photo."
        });
      }
    };

    analyzeFace();
  }, [image, onAnalysisComplete, onLoadingChange, performAnalysis]);

  return null;
}

export default FaceAnalyzer;